import { handleError } from '../../../ErrorHandler/errorHandlerPropsMap';
import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { PublicPlan } from '@wix/ambassador-pricing-plans-v2-plan/types';
import { Plan as PlanV3 } from '@wix/ambassador-pricing-plans-v3-plan/types';
import {
  BenefitWithPlanInfo,
  Benefit,
  PlanInfo,
  PlanStatus,
} from '@wix/ambassador-pricing-plan-benefits-server/types';
import { WarmupData } from '../../../../services/WarmupData';
import { IBenefitsListResponse } from '../interfaces';
import { defaultErrorOptions } from '../../../../utils/defaultErrorOptions';
import { isPricingPlansV3Enabled } from '../../../../experiments/isPricingPlansV3Enabled';
import { IUserPaidPlan } from '@wix/challenges-web-library';

type WithDetailsId<T> = T & {
  details: { id: string };
};

const transformV2UserPlan = (
  plan: PublicPlan,
  planInfo: PlanInfo,
  benefit: Benefit,
): WithDetailsId<IUserPaidPlan> => ({
  id: planInfo.id,
  details: {
    id: planInfo.id,
    name: planInfo.name,
    description: plan?.description || '',
  },
  challenges: benefit.resourceIds,
  paymentOptions: {
    price: {
      amount: plan?.pricing?.price?.value,
      currency: plan?.pricing?.price?.currency,
    },
    additionalFees: plan?.pricing?.feeConfigs?.map(({ fee }) => fee) ?? [],
    recurring: !!plan?.pricing?.subscription,
    validFor: {
      forever: false,
      period: {
        amount: plan?.pricing?.subscription?.cycleDuration?.count,
        unit: plan?.pricing?.subscription?.cycleDuration?.unit,
      },
    },
  },
  visible: planInfo.visible || false,
});

const transformV3UserPlan = (
  plan: PlanV3,
  planInfo: PlanInfo,
  benefit: Benefit,
): WithDetailsId<IUserPaidPlan> => {
  const pricingVariant = plan?.pricingVariants?.[0];
  const billingTerms = pricingVariant?.billingTerms;
  const billingCycleCount = billingTerms?.billingCycle?.count;
  const parsedCount =
    billingCycleCount != null && billingCycleCount !== ''
      ? Number(billingCycleCount)
      : undefined;

  return {
    id: planInfo.id,
    details: {
      id: planInfo.id,
      name: planInfo.name,
      description: plan?.description || '',
    },
    challenges: benefit.resourceIds,
    paymentOptions: {
      price: {
        amount: pricingVariant?.pricingStrategies?.[0]?.flatRate?.amount,
        currency: plan?.currency,
      },
      additionalFees:
        pricingVariant?.fees?.map((fee) => ({
          name: fee.name,
          amount: fee.fixedAmountOptions?.amount,
        })) ?? [],
      recurring: !!billingTerms?.billingCycle,
      validFor: {
        forever: false,
        period: {
          amount: Number.isNaN(parsedCount) ? undefined : parsedCount,
          unit: billingTerms?.billingCycle?.period,
        },
      },
    },
    visible: planInfo.visible || false,
  };
};

const WARMUPDATA_USERS_PP_V3 = 'WARMUPDATA_ELIGIBLE_ORDERS_V3';
const WARMUPDATA_USERS_PP_V2 = 'WARMUPDATA_ELIGIBLE_ORDERS_V2';

const loadUserPaidPlansV2 = async (flowAPI: ControllerFlowAPI) => {
  const warmupData = new WarmupData(flowAPI);
  const ssrData = warmupData.get(WARMUPDATA_USERS_PP_V2);
  try {
    const paidPlansWithBenefits: IBenefitsListResponse =
      ssrData ||
      (
        await flowAPI.errorHandler.withErrorHandler(async () => {
          return flowAPI.httpClient.get(
            '/_serverless/challenges-web-serverless/get-benefits-list',
          );
        }, defaultErrorOptions)
      )?.data;

    warmupData.set(WARMUPDATA_USERS_PP_V2, paidPlansWithBenefits);

    const benefitsActive: BenefitWithPlanInfo[] =
      paidPlansWithBenefits?.benefits?.filter(
        ({ planInfo }) => planInfo.status === PlanStatus.ACTIVE,
      ) || [];

    return benefitsActive
      .map(({ planInfo, benefit }) => {
        const plan = paidPlansWithBenefits.paidPlans.find(({ id }) => {
          return id === planInfo.id;
        });

        return transformV2UserPlan(plan as PublicPlan, planInfo, benefit);
      })
      .filter((plan) => !!plan);
  } catch (error) {
    const resolvedError = flowAPI.errorHandler.getResolvedError(error);
    handleError({ resolvedError, error, context: 'loadUserPaidPlans' });
    console.error(error);
  }
};

const loadUserPaidPlansV3 = async (flowAPI: ControllerFlowAPI) => {
  const warmupData = new WarmupData(flowAPI);
  const ssrData = warmupData.get(WARMUPDATA_USERS_PP_V3);
  try {
    const paidPlansWithBenefits: IBenefitsListResponse =
      ssrData ||
      (
        await flowAPI.errorHandler.withErrorHandler(async () => {
          return flowAPI.httpClient.get(
            '/_serverless/challenges-web-serverless/get-benefits-list',
          );
        }, defaultErrorOptions)
      )?.data;

    warmupData.set(WARMUPDATA_USERS_PP_V3, paidPlansWithBenefits);

    const benefitsActive: BenefitWithPlanInfo[] =
      paidPlansWithBenefits?.benefits?.filter(
        ({ planInfo }) => planInfo.status === PlanStatus.ACTIVE,
      ) || [];

    return benefitsActive
      .map(({ planInfo, benefit }) => {
        const plan = paidPlansWithBenefits.paidPlans.find(({ id }) => {
          return id === planInfo.id;
        });

        return transformV3UserPlan(plan as PlanV3, planInfo, benefit);
      })
      .filter((plan) => !!plan);
  } catch (error) {
    const resolvedError = flowAPI.errorHandler.getResolvedError(error);
    handleError({ resolvedError, error, context: 'loadUserPaidPlans' });
    console.error(error);
  }
};

export const loadUserPaidPlans = async (flowAPI: ControllerFlowAPI) => {
  return isPricingPlansV3Enabled(flowAPI)
    ? loadUserPaidPlansV3(flowAPI)
    : loadUserPaidPlansV2(flowAPI);
};
